import {
  DELIVERY_IMPORT_FIELDS,
  nationalAddressSchema,
  type DeliveryImportField,
  type ImportDeliveryRowInput,
  type SenderType,
  type ShipmentPaymentType,
} from "@mandoob/shared";

import { normalizePhoneInput } from "@/lib/phone-utils";

import type { SheetData, SheetRow } from "./spreadsheet";

/** Column index (into `SheetData.headers`) chosen for each delivery field. */
export type ColumnMapping = Partial<Record<DeliveryImportField, number>>;

/** A template variable is filled from a column, or from one value for the whole file. */
export type VariableSource =
  | { kind: "column"; column: number }
  | { kind: "fixed"; value: string };

export type VariableMapping = Record<string, VariableSource>;

export type InvalidRowReason =
  | { kind: "MissingPhone" }
  | { kind: "InvalidPhone"; value: string }
  | { kind: "DuplicatePhone" }
  | { kind: "InvalidDriverPhone"; value: string }
  | { kind: "MissingVariable"; variable: string };

export interface InvalidRow {
  rowNumber: number;
  /** Best-effort label for the row: the mapped name, else the raw phone cell. */
  label: string;
  reason: InvalidRowReason;
}

export interface BuiltRows {
  valid: ImportDeliveryRowInput[];
  invalid: InvalidRow[];
}

// ---------------------------------------------------------------------------
// Column matching
// ---------------------------------------------------------------------------

/**
 * Header aliases per field, Arabic and English. Matching is longest-alias-wins,
 * which is what keeps a "driver phone" / "رقم المندوب" column on
 * `driverCallingPhone` instead of the shorter client "phone" / "رقم".
 */
const FIELD_ALIASES: Record<DeliveryImportField, string[]> = {
  clientPhone: [
    "phone",
    "phone number",
    "mobile",
    "mobile number",
    "whatsapp",
    "whatsapp number",
    "tel",
    "telephone",
    "client phone",
    "customer phone",
    "recipient phone",
    "جوال",
    "الجوال",
    "رقم",
    "الرقم",
    "رقم الجوال",
    "رقم الهاتف",
    "هاتف",
    "الهاتف",
    "موبايل",
    "واتساب",
    "الواتساب",
    "رقم العميل",
    "جوال العميل",
    "رقم المستلم",
    "جوال المستلم",
  ],
  clientName: [
    "name",
    "client",
    "client name",
    "customer",
    "customer name",
    "recipient",
    "recipient name",
    "receiver",
    "اسم",
    "الاسم",
    "العميل",
    "اسم العميل",
    "الزبون",
    "اسم الزبون",
    "المستلم",
    "اسم المستلم",
  ],
  trackingNumber: [
    "tracking",
    "tracking number",
    "tracking no",
    "awb",
    "waybill",
    "shipment",
    "shipment number",
    "order",
    "order number",
    "reference",
    "تتبع",
    "التتبع",
    "رقم التتبع",
    "شحنة",
    "الشحنة",
    "رقم الشحنة",
    "بوليصة",
    "رقم البوليصة",
    "طلب",
    "رقم الطلب",
    "المرجع",
  ],
  company: [
    "company",
    "brand",
    "store",
    "merchant",
    "shipper",
    "sender",
    "شركة",
    "الشركة",
    "المتجر",
    "التاجر",
    "العلامة",
    "المرسل",
  ],
  driverName: [
    "driver",
    "driver name",
    "courier",
    "courier name",
    "agent",
    "مندوب",
    "المندوب",
    "اسم المندوب",
    "السائق",
    "اسم السائق",
  ],
  driverCallingPhone: [
    "driver phone",
    "driver mobile",
    "driver number",
    "courier phone",
    "courier number",
    "contact number",
    "جوال المندوب",
    "رقم المندوب",
    "هاتف المندوب",
    "جوال السائق",
    "رقم السائق",
  ],
  // Extra shipment facts — matched only when the file plainly has them.
  paymentType: [
    "payment",
    "payment type",
    "payment method",
    "cod",
    "cash on delivery",
    "الدفع",
    "طريقة الدفع",
    "نوع الدفع",
    "الدفع عند الاستلام",
  ],
  cost: [
    "cost",
    "price",
    "amount",
    "total",
    "cod amount",
    "order value",
    "value",
    "التكلفة",
    "السعر",
    "المبلغ",
    "الإجمالي",
    "قيمة الطلب",
    "مبلغ التحصيل",
  ],
  shippingCost: [
    "shipping cost",
    "shipping fee",
    "shipping",
    "delivery fee",
    "delivery cost",
    "freight",
    "تكلفة الشحن",
    "رسوم الشحن",
    "الشحن",
    "رسوم التوصيل",
    "تكلفة التوصيل",
  ],
  senderName: [
    "sender name",
    "shipper name",
    "merchant name",
    "store name",
    "seller",
    "اسم المرسل",
    "اسم التاجر",
    "اسم المتجر",
    "البائع",
  ],
  senderWebsite: [
    "website",
    "sender website",
    "store website",
    "url",
    "الموقع",
    "موقع المتجر",
    "الموقع الإلكتروني",
  ],
  weight: ["weight", "kg", "الوزن", "وزن"],
  nationalAddress: [
    "national address",
    "short address",
    "address code",
    "العنوان الوطني",
    "العنوان المختصر",
    "رمز العنوان",
  ],
  clientCity: [
    "city",
    "client city",
    "customer city",
    "recipient city",
    "المدينة",
    "مدينة العميل",
    "مدينة المستلم",
  ],
};

/** Lowercased, punctuation-free, with the Arabic letter forms unified. */
function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .replace(/[ـً-ْ]/g, "") // tatweel + harakat
    .replace(/[أإآ]/g, "ا") // hamza forms -> alef
    .replace(/ة/g, "ه") // ta marbuta -> ha
    .replace(/ى/g, "ي") // alef maqsura -> ya
    .replace(/[_\-./\\#()[\]{}:*]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Aliases run through the same normalizer as the headers, or an alias written
 * with a ta marbuta could never match the header it was written for.
 */
const NORMALIZED_ALIASES = Object.fromEntries(
  Object.entries(FIELD_ALIASES).map(([field, aliases]) => [
    field,
    [...new Set(aliases.map(normalizeHeader))],
  ]),
) as Record<DeliveryImportField, string[]>;

function score(header: string, alias: string): number {
  if (header === alias) return 1000 + alias.length;
  if (header.includes(alias)) return 100 + alias.length;
  return 0;
}

/**
 * Best-first assignment of headers to fields: each header feeds at most one
 * field and each field takes at most one header, so a file carrying both
 * "phone" and "driver phone" splits them instead of double-booking a column.
 */
export function guessColumnMapping(headers: string[]): ColumnMapping {
  const normalized = headers.map(normalizeHeader);
  const candidates: {
    field: DeliveryImportField;
    column: number;
    weight: number;
  }[] = [];

  for (const { key } of DELIVERY_IMPORT_FIELDS) {
    for (const [column, header] of normalized.entries()) {
      const best = Math.max(
        0,
        ...NORMALIZED_ALIASES[key].map((alias) => score(header, alias)),
      );
      if (best > 0) candidates.push({ field: key, column, weight: best });
    }
  }
  candidates.sort((a, b) => b.weight - a.weight);

  const mapping: ColumnMapping = {};
  const takenColumns = new Set<number>();
  for (const candidate of candidates) {
    if (mapping[candidate.field] !== undefined) continue;
    if (takenColumns.has(candidate.column)) continue;
    mapping[candidate.field] = candidate.column;
    takenColumns.add(candidate.column);
  }
  return mapping;
}

/** Template variables auto-match on an exact header name only ("1", "2", "name"). */
export function guessVariableMapping(
  variables: string[],
  headers: string[],
  used: ColumnMapping,
): VariableMapping {
  const normalized = headers.map(normalizeHeader);
  const taken = new Set(Object.values(used));
  const mapping: VariableMapping = {};
  for (const variable of variables) {
    const column = normalized.indexOf(normalizeHeader(variable));
    if (column >= 0 && !taken.has(column)) {
      mapping[variable] = { kind: "column", column };
      taken.add(column);
    }
  }
  return mapping;
}

// ---------------------------------------------------------------------------
// Row building / validation
// ---------------------------------------------------------------------------

/** WhatsApp rejects newlines and tabs inside template variables. */
const flatten = (value: string): string =>
  value.replace(/\s+/g, " ").trim().slice(0, 1024);

function cell(row: SheetRow, column: number | undefined): string {
  return column === undefined ? "" : (row.cells[column] ?? "").trim();
}

/** Empty cells are omitted so the server falls back to the driver profile. */
function optional(key: string, value: string, max: number) {
  const trimmed = value.trim().slice(0, max);
  return trimmed ? { [key]: trimmed } : {};
}

// ---------------------------------------------------------------------------
// Extra shipment facts: parsed leniently, dropped silently when unreadable.
// A file that says "150 SAR", "٢.٥ كجم" or "COD" gets stored; one that says
// nothing (or nonsense) simply leaves the field null — never an error row.
// ---------------------------------------------------------------------------

const asciiDigits = (value: string): string =>
  value
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)));

function firstNumber(value: string): number | null {
  const match = /-?\d+(?:\.\d+)?/.exec(asciiDigits(value).replace(/,/g, ""));
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Money in SAR (decimal) -> integer halalas. */
function halalas(value: string): number | null {
  const amount = firstNumber(value);
  if (amount === null || amount < 0 || amount > 10_000_000) return null;
  return Math.round(amount * 100);
}

/** "2.5", "2.5 kg", "2500 g" -> grams (bare numbers are kg below 500). */
function grams(value: string): number | null {
  const amount = firstNumber(value);
  if (amount === null || amount <= 0) return null;
  const text = value.toLowerCase();
  const inGrams =
    /(^|[^a-z])(g|gm|gr|gram|grams)([^a-z]|$)|جم|جرام|غرام/.test(text) ||
    amount >= 500;
  const result = Math.round(inGrams ? amount : amount * 1000);
  return result > 0 && result <= 100_000_000 ? result : null;
}

function paymentType(value: string): ShipmentPaymentType | null {
  const text = value.trim().toLowerCase();
  if (!text) return null;
  if (/cod|cash|collect|الدفع عند|عند الاستلام|نقد/.test(text)) return "COD";
  if (
    /paid|prepaid|pre-paid|card|online|مدفوع|مسبق|بطاقة|اونلاين|أونلاين/.test(
      text,
    )
  ) {
    return "Paid";
  }
  return "Other";
}

function website(value: string): string | null {
  const candidate = value.replace(/\s+/g, "");
  const bare = candidate.replace(/^https?:\/\//i, "");
  return /^[\w-]+(\.[\w-]+)+/.test(bare) ? candidate.slice(0, 200) : null;
}

function nationalAddress(value: string): string | null {
  const parsed = nationalAddressSchema.safeParse(asciiDigits(value));
  return parsed.success ? parsed.data : null;
}

/** The parsed extras for one row — only the keys that produced a value. */
function extraFields(row: SheetRow, mapping: ColumnMapping) {
  const out: Record<string, string | number> = {};
  const set = (key: string, value: string | number | null | undefined) => {
    if (value !== null && value !== undefined && value !== "") out[key] = value;
  };
  set("paymentType", paymentType(cell(row, mapping.paymentType)));
  set("costHalalas", halalas(cell(row, mapping.cost)));
  set("shippingCostHalalas", halalas(cell(row, mapping.shippingCost)));
  set("senderName", cell(row, mapping.senderName).slice(0, 120));
  const site = website(cell(row, mapping.senderWebsite));
  set("senderWebsite", site);
  // A sender with a website is a business; the server applies the same rule
  // but keeping the row self-describing costs nothing.
  if (site) set("senderType", "Company" satisfies SenderType);
  set("weightGrams", grams(cell(row, mapping.weight)));
  set("nationalAddress", nationalAddress(cell(row, mapping.nationalAddress)));
  set("clientCity", cell(row, mapping.clientCity).slice(0, 120));
  return out;
}

/**
 * Turns mapped sheet rows into API payloads, splitting off the rows that cannot
 * be sent so the UI can show exactly which line is wrong and why. Duplicate
 * client phones are rejected after the first: a second delivery for the same
 * client would only reset the first one.
 */
export function buildImportRows(
  sheet: SheetData,
  mapping: ColumnMapping,
  variables: VariableMapping,
): BuiltRows {
  const valid: ImportDeliveryRowInput[] = [];
  const invalid: InvalidRow[] = [];
  const seenPhones = new Set<string>();

  for (const row of sheet.rows) {
    const rawPhone = cell(row, mapping.clientPhone);
    const name = cell(row, mapping.clientName);
    const label = name || rawPhone || `#${row.rowNumber}`;
    const reject = (reason: InvalidRowReason) =>
      invalid.push({ rowNumber: row.rowNumber, label, reason });

    if (!rawPhone) {
      reject({ kind: "MissingPhone" });
      continue;
    }
    const clientPhone = normalizePhoneInput(rawPhone);
    if (!clientPhone) {
      reject({ kind: "InvalidPhone", value: rawPhone });
      continue;
    }
    if (seenPhones.has(clientPhone)) {
      reject({ kind: "DuplicatePhone" });
      continue;
    }

    const rawDriverPhone = cell(row, mapping.driverCallingPhone);
    const driverCallingPhone = rawDriverPhone
      ? normalizePhoneInput(rawDriverPhone)
      : null;
    if (rawDriverPhone && !driverCallingPhone) {
      reject({ kind: "InvalidDriverPhone", value: rawDriverPhone });
      continue;
    }

    const values: Record<string, string> = {};
    let missingVariable: string | null = null;
    for (const [variable, source] of Object.entries(variables)) {
      const value = flatten(
        source.kind === "fixed" ? source.value : cell(row, source.column),
      );
      if (!value) {
        missingVariable = variable;
        break;
      }
      values[variable] = value;
    }
    if (missingVariable) {
      reject({ kind: "MissingVariable", variable: missingVariable });
      continue;
    }

    seenPhones.add(clientPhone);
    valid.push({
      rowNumber: row.rowNumber,
      clientPhone,
      ...optional("clientName", name, 120),
      ...optional("trackingNumber", cell(row, mapping.trackingNumber), 100),
      ...optional("company", cell(row, mapping.company), 120),
      ...optional("driverName", cell(row, mapping.driverName), 120),
      ...(driverCallingPhone ? { driverCallingPhone } : {}),
      ...extraFields(row, mapping),
      variables: values,
    });
  }

  return { valid, invalid };
}
